import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Plus, Save, Send, Trash2 } from 'lucide-react'
import { Alert, Card, Field, Input, Loading, Select, Textarea, useToast } from '@/components/ui'
import {
  AttachmentCollector, MaterialPicker, NotPermitted, queueAttachments, type PendingAttachment,
} from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import {
  fetchLines, fetchOne, issuedAgainstIndent, stockOnHand, useMaterials, useSites,
} from '@/lib/data'
import { can, isHoRole, iso, round2 } from '@/lib/domain'
import { qty, rupees } from '@/lib/format'
import { enqueue, reviseDocument } from '@/lib/sync'
import { supabase } from '@/lib/supabase'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import type { Material } from '@/lib/types'

// The document rows come back untyped from PostgREST; the form owns the shape.
type Any = Record<string, any>

interface Line {
  key: string
  material_id: string | null
  quantity_requested: string
  estimated_rate: string
  specifications: string
  required_by_date: string
  on_hand: number | null
}

const blank = (): Line => ({
  key: crypto.randomUUID(), material_id: null, quantity_requested: '',
  estimated_rate: '', specifications: '', required_by_date: '', on_hand: null,
})

/**
 * Raise a requisition — or correct one the Head of Stores sent back.
 *
 * The same form does both. In edit mode it loads the existing document, keeps
 * its number and its audit trail, and rewrites the lines in place.
 */
export function PrNew() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const { id: editId } = useParams()
  const fromIndent = params.get('from_indent')
  const editing = !!editId
  const [loadingDoc, setLoadingDoc] = useState(editing)
  // If the document could not be opened for correction, do not offer a form —
  // an empty form invites the user to retype everything and lose the original.
  const [editBlocked, setEditBlocked] = useState(false)
  const [prType, setPrType] = useState<string>('STANDARD')
  // Explains why the requisition carries less than the indent asked for.
  const [indentNote, setIndentNote] = useState<string | null>(null)

  const role = profile!.role
  const ho = isHoRole(role)
  const { data: sites } = useSites()
  const { data: materials } = useMaterials()

  const [siteId, setSiteId] = useState(profile!.site_id ?? 'HO')
  const [urgency, setUrgency] = useState('Normal')
  const [docDate, setDocDate] = useState(todayIso)
  const [requiredBy, setRequiredBy] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return iso(d)
  })
  const [remarks, setRemarks] = useState('')
  const [lines, setLines] = useState<Line[]>([blank()])
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  // Pre-load the RCM's indent when the store-man converts one.
  useEffect(() => {
    if (!fromIndent) return
    void (async () => {
      try {
        const header = await fetchOne<{
          site_id: string; required_by: string; work_description: string; indent_date: string
        }>('indents', fromIndent)
        const rows = await fetchLines<{ material_id: string; quantity: number; required_by: string; remarks: string }>(
          'indent_line_items', 'indent_id', fromIndent)
        if (header) {
          setSiteId(header.site_id)
          // Carry the indent's date forward so the whole chain tallies. The
          // store-man raises the requisition the same day he receives the
          // indent, and when he is catching up weeks later both documents
          // should sit on the day the site actually asked, not on the day he
          // found time to type. He can still change it.
          if (header.indent_date) setDocDate(header.indent_date)
          if (header.required_by) setRequiredBy(header.required_by)
          setRemarks(`Raised against site indent ${fromIndent}${header.work_description ? ' — ' + header.work_description : ''}`)
        }
        if (rows.length) {
          const site = header?.site_id ?? profile!.site_id ?? 'HO'

          // Anything already handed over on an approved issue note against this
          // indent is settled. Asking procurement to buy it again would order
          // the same material twice and pay for it twice.
          const alreadyIssued = await issuedAgainstIndent(fromIndent)

          const balanceLines = rows
            .map((r) => ({ r, balance: Number(r.quantity) - (alreadyIssued[r.material_id] ?? 0) }))
            .filter(({ balance }) => balance > 0.001)

          if (balanceLines.length === 0) {
            toast.warn('Everything on this indent has already been issued from the store. There is nothing left to requisition.')
            setLines([blank()])
          } else {
            // Read live stock too, so "In your store now" appears on lines the
            // store-man did not pick by hand.
            setLines(await Promise.all(balanceLines.map(async ({ r, balance }) => ({
              key: crypto.randomUUID(),
              material_id: r.material_id,
              quantity_requested: String(round2(balance)),
              estimated_rate: '',
              specifications: r.remarks ?? '',
              required_by_date: r.required_by ?? '',
              on_hand: r.material_id ? await stockOnHand(site, r.material_id) : null,
            }))))

            if (balanceLines.length < rows.length) {
              setIndentNote(
                `${rows.length - balanceLines.length} material(s) on this indent were already issued from the store and are not listed here.`,
              )
            }
          }
        }
      } catch (e) {
        toast.warn('Could not load the indent; you can still enter the requisition by hand.')
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromIndent])

  // Correcting a returned requisition: load it, check we are allowed, prefill.
  useEffect(() => {
    if (!editId) return
    void (async () => {
      setLoadingDoc(true)
      try {
        const { error: guard } = await supabase.rpc('rpc_assert_editable', {
          p_doc_type: 'PR', p_doc_id: editId,
        })
        if (guard) throw new Error(guard.message)

        const header = await fetchOne<Any>('purchase_requisitions', editId)
        if (!header) throw new Error(`Requisition ${editId} was not found.`)
        const rows = await fetchLines<Any>('pr_line_items', 'pr_id', editId)

        setSiteId(header.site_id)
        setPrType(header.pr_type ?? 'STANDARD')
        setUrgency(header.urgency ?? 'Normal')
        if (header.required_by_date) setRequiredBy(header.required_by_date)
        // Show the requisition's own date when correcting it, not today's.
        if (header.date_created) setDocDate(header.date_created)
        setRemarks(header.remarks ?? '')
        setLines(await Promise.all((rows ?? []).map(async (r: Any) => ({
          key: crypto.randomUUID(),
          material_id: r.material_id,
          quantity_requested: String(r.quantity_requested ?? ''),
          estimated_rate: r.estimated_rate ? String(r.estimated_rate) : '',
          specifications: r.specifications ?? '',
          required_by_date: r.required_by_date ?? '',
          on_hand: r.material_id ? await stockOnHand(header.site_id, r.material_id) : null,
        }))))
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not open this requisition for correction')
        setEditBlocked(true)
      } finally {
        setLoadingDoc(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  const materialById = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>,
    [materials])

  const total = round2(lines.reduce(
    (a, l) => a + (Number(l.quantity_requested) || 0) * (Number(l.estimated_rate) || 0), 0))

  if (!can(role, 'createPr')) {
    return <NotPermitted what="raise a purchase requisition" who="site Store-man (or the HO Stores Executive on their behalf)" />
  }

  function update(key: string, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  async function pickMaterial(key: string, m: Material | null) {
    update(key, {
      material_id: m?.id ?? null,
      on_hand: null,
      estimated_rate: lines.find((l) => l.key === key)?.estimated_rate || '',
    })
    if (m) {
      const oh = await stockOnHand(siteId, m.id)
      update(key, { on_hand: oh })
    }
  }

  function validate(): string | null {
    const filled = lines.filter((l) => l.material_id && Number(l.quantity_requested) > 0)
    if (filled.length === 0) return 'Add at least one material with a quantity.'
    const ids = filled.map((l) => l.material_id)
    if (new Set(ids).size !== ids.length) return 'The same material appears more than once. Combine those lines.'
    if (!requiredBy) return 'Tell the Head of Stores by when the material is needed.'
    return null
  }

  async function save(submit: boolean) {
    const problem = validate()
    if (problem) {
      setError(problem)
      // Take him to it. The Submit button sits at the foot of a form that can
      // run to several screens, so an error drawn at the top is, from where he
      // is standing, nothing happening at all. Reported on the goods receipt
      // screen 09-09-2026; the same shape of form, the same trap.
      requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
      return
    }
    setError(null)
    setBusy(true)
    try {
      const filled = lines.filter((l) => l.material_id && Number(l.quantity_requested) > 0)

      const lineRows = filled.map((l, i) => ({
        sl_no: i + 1,
        material_id: l.material_id,
        quantity_requested: Number(l.quantity_requested),
        uom: materialById[l.material_id!]?.unit_of_measure ?? null,
        estimated_rate: Number(l.estimated_rate) || 0,
        line_amount: round2((Number(l.quantity_requested) || 0) * (Number(l.estimated_rate) || 0)),
        specifications: l.specifications || null,
        required_by_date: l.required_by_date || requiredBy,
      }))

      // ---- correcting a returned requisition --------------------------------
      if (editing) {
        await reviseDocument({
          docType: 'PR',
          docId: editId!,
          header: {
            urgency,
            required_by_date: requiredBy,
            total_estimated_amount: total,
            remarks: remarks || null,
          },
          lines: lineRows,
          submit,
          comment: submit ? 'Corrected and resubmitted after being returned' : undefined,
        })
        if (attachments.length) await queueAttachments('PR', editId!, attachments)
        toast.success(submit
          ? 'Corrected requisition sent back to the Head of Stores.'
          : 'Correction saved. It stays with you until you submit it.')
        navigate(`/prs/${editId}`)
        return
      }

      const payload = {
        header: {
          site_id: siteId,
          pr_type: 'STANDARD',
          status: submit ? 'SUBMITTED' : 'DRAFT',
          urgency,
          date_created: docDate,
          required_by_date: requiredBy,
          requested_by: profile!.id,
          requested_by_code: profile!.user_code,
          creator_email: profile!.email,
          total_estimated_amount: total,
          remarks: remarks || null,
          source_indent_id: fromIndent || null,
          was_submitted: submit,
        },
        lines: lineRows,
        source_indent_id: fromIndent || null,
      }

      const localId = `PR-LOCAL-${Date.now()}`
      await enqueue({
        kind: 'pr.create',
        docType: 'PR',
        localDocId: localId,
        label: `${submit ? 'Submit' : 'Save'} requisition for ${siteId} (${filled.length} items)`,
        payload,
      })
      if (attachments.length) await queueAttachments('PR', localId, attachments)

      toast.success(
        navigator.onLine
          ? `Requisition ${submit ? 'submitted to the Head of Stores' : 'saved as a draft'}.`
          : 'Saved on this device. It will be sent as soon as you are back online.',
      )
      navigate('/prs')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the requisition')
    } finally {
      setBusy(false)
    }
  }

  if (loadingDoc) return <Loading label="Opening the requisition…" />

  if (editBlocked) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="This requisition cannot be corrected">{error}</Alert>
        <button className="btn-secondary" onClick={() => navigate(`/prs/${editId}`)}>
          Back to the requisition
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card
        title={editing ? `Correct requisition ${editId}` : 'New purchase requisition'}
        subtitle={editing
          ? 'Make the changes the Head of Stores asked for, then send it back. The requisition keeps its number and its history.'
          : 'Consolidate everything the site needs that you cannot fulfil from your own store'}
      >
        <div ref={errorRef}>{error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}</div>
        {indentNote && (
          <div className="mb-4">
            <Alert tone="info" title="Only the balance of the indent is listed here">
              {indentNote} You are requisitioning what the store could not supply.
            </Alert>
          </div>
        )}
        {editing && prType === 'IST' && (
          <div className="mb-4">
            <Alert tone="info" title="Inter-site transfer requisition">
              This requisition moves stock between sites rather than buying it. Changing a line here
              changes what the receiving site is promised.
            </Alert>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Site" required hint={ho ? 'HO staff may raise on behalf of a site' : 'Your posted site'}>
            <Select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              disabled={!ho || editing}
              options={sites.map((s) => ({ value: s.id, label: `${s.site_name} (${s.site_code})` }))}
            />
          </Field>
          <Field label="Urgency" required>
            <Select
              value={urgency} onChange={(e) => setUrgency(e.target.value)}
              options={['Low', 'Normal', 'High', 'Critical'].map((u) => ({ value: u, label: u }))}
            />
          </Field>
          <DocumentDate label="Requisition date" value={docDate} onChange={setDocDate} />
          <Field label="Needed at site by" required>
            <Input type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} />
          </Field>
          <Field label="Estimated value" hint="Indicative only — the real rate comes from the vendor quotation">
            <div className="field bg-slate-50 font-semibold tabular-nums">{rupees(total)}</div>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Remarks for the Head of Stores"
                 hint="What the material is for. This helps him decide between an inter-site transfer and a fresh purchase.">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      placeholder="e.g. Slab casting at block B, scheduled 12 Aug" />
          </Field>
        </div>
      </Card>

      <Card
        title="Materials required"
        subtitle="Current stock at your site is shown so you only ask for the shortfall"
        actions={<button className="btn-secondary btn-sm" onClick={() => /* prepend: the blank row appears at the top so nobody scrolls to the bottom of a long document to type the next line. Agreed 09-09-2026. */ setLines((l) => [blank(), ...l])}>
          <Plus className="h-4 w-4" /> Add line
        </button>}
      >
        <div className="space-y-3">
          {lines.map((l, i) => {
            const m = l.material_id ? materialById[l.material_id] : null
            const qtyNum = Number(l.quantity_requested) || 0
            const shortfall = l.on_hand !== null ? qtyNum - l.on_hand : null
            return (
              <div key={l.key} className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">LINE {i + 1}</span>
                  {lines.length > 1 && (
                    <button
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                      aria-label="Remove line"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="grid gap-3 lg:grid-cols-12">
                  <Field label="Material" required className="lg:col-span-5">
                    <MaterialPicker
                      value={l.material_id}
                      onChange={(m) => pickMaterial(l.key, m)}
                      exclude={lines.filter((x) => x.key !== l.key).map((x) => x.material_id!).filter(Boolean)}
                      autoFocus={false}
                    />
                    {l.on_hand !== null && m && (
                      <p className={`hint ${l.on_hand > 0 ? 'text-emerald-700' : ''}`}>
                        In your store now: <strong>{qty(l.on_hand)} {m.unit_of_measure}</strong>
                        {shortfall !== null && shortfall > 0 && ` · shortfall ${qty(shortfall)} ${m.unit_of_measure}`}
                      </p>
                    )}
                  </Field>

                  <Field label={`Quantity${m ? ` (${m.unit_of_measure})` : ''}`} required className="lg:col-span-2">
                    <Input
                      type="number" inputMode="decimal" min="0" step="any"
                      value={l.quantity_requested}
                      onChange={(e) => update(l.key, { quantity_requested: e.target.value })}
                    />
                  </Field>

                  <Field label={`Estimated rate${m ? ` per ${m.unit_of_measure}` : ''}`} hint="Optional" className="lg:col-span-2">
                    <Input
                      type="number" inputMode="decimal" min="0" step="any"
                      value={l.estimated_rate}
                      onChange={(e) => update(l.key, { estimated_rate: e.target.value })}
                    />
                  </Field>

                  <Field label="Needed by" className="lg:col-span-3">
                    <Input type="date" value={l.required_by_date || requiredBy}
                           onChange={(e) => update(l.key, { required_by_date: e.target.value })} />
                  </Field>

                  <Field label="Specification / make" className="lg:col-span-12">
                    <Input
                      value={l.specifications}
                      onChange={(e) => update(l.key, { specifications: e.target.value })}
                      placeholder="e.g. Fe500D, IS 1786:2008, test certificate required"
                    />
                  </Field>
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      <Card title="Supporting documents"
            subtitle="Anything that helps the Head of Stores decide — the RCM's written request, a quotation, a photograph of what has failed">
        <AttachmentCollector docType="PR" value={attachments} onChange={setAttachments} />
        {editing && (
          <p className="mt-2 text-xs text-slate-500">
            Files already on this requisition stay where they are. Anything you add here is added to them.
          </p>
        )}
      </Card>

      <div className="sticky bottom-20 z-10 flex flex-wrap justify-end gap-2 lg:bottom-4">
        <button className="btn-secondary" onClick={() => navigate(editing ? `/prs/${editId}` : '/prs')} disabled={busy}>
          Cancel
        </button>
        <button className="btn-secondary" onClick={() => save(false)} disabled={busy}>
          <Save className="h-4 w-4" /> {editing ? 'Save without submitting' : 'Save draft'}
        </button>
        <button className="btn-primary" onClick={() => save(true)} disabled={busy}>
          <Send className="h-4 w-4" /> {editing ? 'Resubmit to Head of Stores' : 'Submit to Head of Stores'}
        </button>
      </div>
    </div>
  )
}
